import { type FormEvent, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ApiError } from '../lib/api.js'
import { useAuth } from '../lib/auth-context.js'
import { LOGIN_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'

type LocationState = { from?: { pathname?: string } }

export function LoginPage() {
  const { signInWithEmail, signInWithGoogle } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as LocationState | null)?.from?.pathname ?? '/'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await signInWithEmail(email, password)
      navigate(from, { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ACCOUNT_FROZEN') {
        navigate('/account/frozen', { replace: true })
        return
      }
      const msg = toUserMessage(err, LOGIN_ERROR_FALLBACK)
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const onGoogleClick = async () => {
    setError(null)
    try {
      await signInWithGoogle()
    } catch (err) {
      setError(toUserMessage(err, LOGIN_ERROR_FALLBACK))
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">テラファイル便にログイン</h1>
          <p className="text-sm text-muted-foreground mt-1">アカウントにログインしてください</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              メールアドレス
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              パスワード
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2 px-4 bg-foreground text-background rounded-md font-medium disabled:opacity-50"
          >
            {submitting ? 'ログイン中…' : 'ログイン'}
          </button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">または</span>
          </div>
        </div>

        <button
          type="button"
          onClick={onGoogleClick}
          className="w-full py-2 px-4 border border-border rounded-md font-medium"
        >
          Google でログイン
        </button>

        <p className="text-sm text-center text-muted-foreground">
          アカウント未作成？{' '}
          <Link to="/signup" className="underline font-medium">
            アカウント登録
          </Link>
        </p>
      </div>
    </div>
  )
}
